import {useState} from 'react';
import {format, subDays} from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  ComposedChart,
} from 'recharts';

import {useGetQuestionMatchesQualityQuery} from '~/store/rtkQuery';

const getStrategyLabel = (strategy: string): string => {
  // Strip "ask-luna-question-queue" from the strategy name
  let label = strategy.replace('system/ask-luna-question-queue', '').trim();

  // Remove leading/trailing slashes
  label = label.replace(/^\/+|\/+$/g, '');

  // If empty after stripping, return the original strategy
  return label || strategy;
};

export const QuestionMatchesQuality = () => {
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 30), 'yyyy-MM-dd'),
  );

  const {data, error, isLoading, refetch} = useGetQuestionMatchesQualityQuery({
    startDate,
    endDate,
  });

  if (error) {
    return (
      <div className="p-4">
        <div role="alert" className="alert alert-error">
          <span>Error loading data</span>
        </div>
        <button className="btn btn-primary mt-4" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 flex items-center justify-center h-96">
        <span className="loading loading-dots loading-lg"></span>
      </div>
    );
  }

  // Filter out strategies with 0 auto-populated questions
  const activeStrategies =
    data?.byStrategy.filter(
      strategy => strategy.summary.numAutoPopulated > 0,
    ) || [];

  // Get all unique days across all strategies and sort them
  const allDays = new Set<string>();
  activeStrategies.forEach(strategy => {
    strategy.data.forEach(day => {
      allDays.add(day.day);
    });
  });

  const sortedDays = Array.from(allDays).sort();

  // Transform data for the chart
  const chartData = sortedDays.map(day => {
    const dataPoint: any = {
      day: format(new Date(day), 'MMM dd'),
      fullDate: day, // Keep full date for sorting
    };

    // Add percentage and count for each strategy
    activeStrategies.forEach(strategy => {
      const strategyDay = strategy.data.find(d => d.day === day);
      if (strategyDay) {
        const totalMatches =
          strategyDay.num_good_match + strategyDay.num_bad_match;
        const percentage =
          totalMatches > 0
            ? (strategyDay.num_good_match / totalMatches) * 100
            : 0;
        dataPoint[strategy.strategy] = Math.round(percentage);
        dataPoint[`${strategy.strategy}_count`] =
          strategyDay.num_auto_populated;
      }
    });

    return dataPoint;
  });

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">
        Question Matches Quality - Good Match %
      </h1>

      {/* Date Range Controls */}
      <div className="flex gap-4 mb-6">
        <div className="form-control">
          <label className="label">
            <span className="label-text">Start Date</span>
          </label>
          <input
            type="date"
            className="input input-bordered"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </div>
        <div className="form-control">
          <label className="label">
            <span className="label-text">End Date</span>
          </label>
          <input
            type="date"
            className="input input-bordered"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <div className="bg-white p-4 rounded-lg shadow">
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis
                yAxisId="left"
                label={{
                  value: 'Good Match Percentage',
                  angle: -90,
                  position: 'insideLeft',
                }}
                domain={[0, 100]}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                label={{
                  value: 'Total Auto Populated',
                  angle: 90,
                  position: 'insideRight',
                }}
              />
              <Tooltip
                content={({active, payload, label}) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                        <p className="font-semibold mb-2">{label}</p>
                        {activeStrategies.map(strategy => {
                          const percentageItem = payload.find(
                            p => p.dataKey === strategy.strategy,
                          );
                          const countItem = payload.find(
                            p => p.dataKey === `${strategy.strategy}_count`,
                          );
                          if (percentageItem || countItem) {
                            return (
                              <div
                                key={strategy.strategy}
                                className="flex items-center gap-2 mb-1">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{
                                    backgroundColor: strategy.color,
                                  }}
                                />
                                <span className="text-sm">
                                  {getStrategyLabel(strategy.strategy)}:{' '}
                                  <strong>{percentageItem?.value}%</strong>
                                  {countItem && (
                                    <span className="text-gray-600 ml-1">
                                      ({countItem.value} total)
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend
                formatter={(value: string) => {
                  // Only show strategy names in legend, not the _count variants
                  if (value.includes('_count')) return null;
                  return getStrategyLabel(value);
                }}
                wrapperStyle={{paddingTop: '20px'}}
              />
              {activeStrategies.map(strategy => (
                <>
                  <Line
                    key={strategy.strategy}
                    yAxisId="left"
                    type="monotone"
                    dataKey={strategy.strategy}
                    stroke={strategy.color}
                    strokeWidth={2}
                    dot={{r: 3}}
                    name={getStrategyLabel(strategy.strategy)}
                  />
                  <Bar
                    key={`${strategy.strategy}_count`}
                    yAxisId="right"
                    dataKey={`${strategy.strategy}_count`}
                    fill={strategy.color}
                    fillOpacity={0.1}
                    name={`${getStrategyLabel(strategy.strategy)} Count`}
                  />
                </>
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="alert alert-info">
          <span>No data available for the selected date range</span>
        </div>
      )}

      {/* Summary Stats Table */}
      {activeStrategies.length > 0 && (
        <div className="mt-6">
          <h2 className="text-xl font-bold mb-4">Summary Statistics</h2>
          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Strategy</th>
                  <th>Total Auto Populated</th>
                  <th>Good Matches</th>
                  <th>Bad Matches</th>
                  <th>% Good Match</th>
                  <th>% Perfect Publish</th>
                </tr>
              </thead>
              <tbody>
                {activeStrategies.map(strategy => (
                  <tr key={strategy.strategy}>
                    <td className="font-medium">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: strategy.color,
                          }}
                        />
                        {getStrategyLabel(strategy.strategy)}
                      </div>
                    </td>
                    <td>
                      {strategy.summary.numAutoPopulated.toLocaleString()}
                    </td>
                    <td>{strategy.summary.numGoodMatch.toLocaleString()}</td>
                    <td>{strategy.summary.numBadMatch.toLocaleString()}</td>
                    <td>{strategy.summary.percentageGoodMatch}%</td>
                    <td>{strategy.summary.percentagePerfectPublish}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
